import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu as MenuIcon, X, Leaf, ShoppingBag, Truck } from "lucide-react";
import { scrollToSection } from "@/lib/scroll";
import { useCart } from "@/context/CartContext";

const LINKS = [
  { label: "Our Story", target: "#story", testid: "nav-story-link" },
  { label: "Menu", target: "#menu", testid: "nav-menu-link" },
  { label: "Gallery", target: "#gallery", testid: "nav-gallery-link" },
  { label: "Reviews", target: "#reviews", testid: "nav-reviews-link" },
  { label: "Visit Us", target: "#contact", testid: "nav-contact-link" },
];

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const { count, setDrawerOpen } = useCart();

  const go = (target) => {
    setOpen(false);
    scrollToSection(target);
  };

  return (
    <header
      data-testid="navbar"
      className="glass-nav fixed inset-x-0 top-0 z-50"
    >
      <div data-testid="delivery-banner" className="bg-[#388E3C] px-4 py-1.5 text-center">
        <p className="flex flex-wrap items-center justify-center gap-x-2 gap-y-0.5 font-grotesk text-[11px] font-medium tracking-wide text-white sm:text-xs">
          <Truck size={13} className="inline" />
          <span>FREE HOME DELIVERY up to 3 KM • Min. order ₹300</span>
          <a href="tel:+919217612215" data-testid="banner-phone-link" className="underline underline-offset-2">
            +91 92176 12215
          </a>
        </p>
      </div>
      <nav className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-5 lg:px-8">
        <button
          data-testid="nav-logo-link"
          onClick={() => go("#home")}
          className="flex items-center gap-3"
        >
          <img
            src="/assets/logo.jpg"
            alt="Podi Sambaram"
            className="h-10 w-auto rounded-md"
          />
          <span className="hidden items-center gap-1 rounded-full border border-[#388E3C]/40 bg-[#388E3C]/10 px-2.5 py-1 font-mono-accent text-[10px] uppercase tracking-widest text-[#4CAF50] sm:flex">
            <Leaf size={11} /> Pure Veg
          </span>
        </button>

        <div className="hidden items-center gap-8 lg:flex">
          {LINKS.map((link) => (
            <button
              key={link.target}
              data-testid={link.testid}
              onClick={() => go(link.target)}
              className="font-grotesk text-sm font-medium text-zinc-300 transition-colors duration-300 hover:text-[#FF9800]"
            >
              {link.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <button
            data-testid="cart-button"
            onClick={() => setDrawerOpen(true)}
            className="relative rounded-full border border-white/10 p-2.5 text-zinc-200 transition-colors hover:border-[#F57C00]/60 hover:text-[#FF9800]"
            aria-label="Open cart"
          >
            <ShoppingBag size={18} />
            {count > 0 && (
              <span
                data-testid="cart-count-badge"
                className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-[#F57C00] font-mono-accent text-[10px] font-bold text-black"
              >
                {count}
              </span>
            )}
          </button>
          <button
            data-testid="nav-reservation-button"
            onClick={() => go("#reserve")}
            className="hidden rounded-full bg-[#F57C00] px-5 py-2.5 font-grotesk text-sm font-semibold text-black transition-all duration-300 hover:bg-[#FF9800] hover:shadow-[0_0_28px_rgba(245,124,0,0.45)] sm:block"
          >
            Reserve a Table
          </button>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen(!open)}
            className="rounded-full border border-white/10 p-2.5 text-zinc-200 lg:hidden"
            aria-label="Toggle menu"
          >
            {open ? <X size={18} /> : <MenuIcon size={18} />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden border-t border-white/5 bg-[#0A0A0C]/95 backdrop-blur-xl lg:hidden"
          >
            <div className="flex flex-col gap-1 px-5 py-4">
              {LINKS.map((link) => (
                <button
                  key={link.target}
                  data-testid={`${link.testid}-mobile`}
                  onClick={() => go(link.target)}
                  className="rounded-lg px-3 py-3 text-left font-grotesk text-base text-zinc-200 transition-colors hover:bg-white/5 hover:text-[#FF9800]"
                >
                  {link.label}
                </button>
              ))}
              <button
                data-testid="nav-reservation-button-mobile"
                onClick={() => go("#reserve")}
                className="mt-2 rounded-full bg-[#F57C00] px-5 py-3 font-grotesk text-sm font-semibold text-black"
              >
                Reserve a Table
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
